import { useCallback, useMemo } from 'react';
import { EngineService } from '@/services/engine';
import { Event } from '@/types/event';
import { PaginatedResponse } from '@/types/responses';
import { useQueryFiltersStore } from '@/stores';
import { SortOption } from '@/utils/query-filters';
import {
    EVENTS_PAGE_SIZE,
    resolveListFetchOrderBy,
    sortOptionToOrderBy,
    useBaseEventFilters,
} from './eventsDataShared';

export interface UseEventsQueryOptions {
    sourceId?: string;
    pageKey?: string;
    sortOption: SortOption;
}

export interface EventsQuery {
    cacheKey: string;
    pageStateKey?: string;
    fetchPage: (pageIndex: number) => Promise<PaginatedResponse<Event>>;
}

export function useEventsQuery({
    sourceId,
    pageKey,
    sortOption,
}: UseEventsQueryOptions): EventsQuery {
    const filters = useQueryFiltersStore((state) => state.filters);
    const selectedAlertRuleIds = useQueryFiltersStore(
        (state) => state.selectedAlertRuleIds,
    );
    const selectedObjectClassIds = useQueryFiltersStore(
        (state) => state.selectedObjectClassIds,
    );
    const baseParams = useBaseEventFilters(sourceId);

    const filterParams = useMemo(() => {
        const params: {
            sourceId?: string[];
            folderId?: string[];
            alertRuleId?: string[];
            classId?: number[];
            minConfidence?: number;
            maxConfidence?: number;
            startTime?: number;
            endTime?: number;
        } = { ...baseParams };

        const selectedAlertRuleIdsArray = Array.from(selectedAlertRuleIds);
        if (selectedAlertRuleIdsArray.length > 0) {
            params.alertRuleId = selectedAlertRuleIdsArray;
        }

        const selectedClassIds = Array.from(selectedObjectClassIds);
        if (selectedClassIds.length > 0) {
            params.classId = selectedClassIds;
        }

        if (filters.confidence.enabled) {
            params.minConfidence = filters.confidence.min;
            params.maxConfidence = filters.confidence.max;
        }

        return params;
    }, [
        baseParams,
        filters.confidence,
        selectedAlertRuleIds,
        selectedObjectClassIds,
    ]);

    const orderBy = sortOptionToOrderBy(sortOption, false);

    const cacheKey = useMemo(() => {
        const parts: string[] = ['events'];
        if (sourceId) parts.push(`source-${sourceId}`);
        parts.push(JSON.stringify(filterParams));
        parts.push(orderBy);
        return parts.join('-');
    }, [sourceId, filterParams, orderBy]);
    const pageStateKey = pageKey ? `${pageKey}:${cacheKey}` : undefined;

    const fetchPage = useCallback(
        async (pageIndex: number): Promise<PaginatedResponse<Event>> => {
            const { hasSourceFilter, hasFolderFilter, effectiveOrderBy } =
                resolveListFetchOrderBy(filterParams, orderBy);

            const baseParams = {
                page: pageIndex + 1,
                size: EVENTS_PAGE_SIZE,
                orderBy: effectiveOrderBy,
            };

            if (hasSourceFilter || hasFolderFilter) {
                return EngineService.searchEvents({
                    ...baseParams,
                    ...filterParams,
                });
            }

            return EngineService.getEvents({
                ...baseParams,
                ...filterParams,
            });
        },
        [filterParams, orderBy],
    );

    return { cacheKey, pageStateKey, fetchPage };
}
